import React, { useState } from 'react';
import { Modal, Table } from 'react-bootstrap';
import moment from 'moment';
import axiosInstance from '../axios';
import { maskTaxId } from '../utils/maskTaxId';

const formatDate = (date) => {
    return date && moment(date).isValid() ? moment(date).utc().format('MM-DD-YYYY') : 'Not available';
};

const formatAmount = (amount) => {
    const numericAmount = Number(String(amount ?? '').replace(/[^\d.-]/g, ''));

    if (Number.isNaN(numericAmount)) return amount || 'Not available';

    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    }).format(numericAmount);
};

const displayValue = (value) => value || 'Not available';

export default function PaymentDetailsModal({ payment, source }) {
    const [show, setShow] = useState(false);
    const [checkSummary, setCheckSummary] = useState(null);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [summaryError, setSummaryError] = useState(false);

    const handleShow = async () => {
        setShow(true);
        setSummaryLoading(true);
        setSummaryError(false);

        try {
            const response = await axiosInstance.post('/payments', {
                page: 1,
                limit: 1,
                source,
                taxId: payment.PAYMENT_TAXID,
                checkNum: payment.CHECKNUM,
                exactCheckNum: true,
            });

            if (!response.data?.success) throw new Error('Unable to load check total');

            setCheckSummary({
                totalAmount: response.data.sumResult?.totalAmount ?? 0,
                paymentCount: response.data.totalCount ?? 0,
            });
        } catch (error) {
            console.error('Error fetching check total:', error);
            setCheckSummary(null);
            setSummaryError(true);
        } finally {
            setSummaryLoading(false);
        }
    };

    return (
        <>
            <button
                type="button"
                className="btn btn-link p-0 align-baseline"
                onClick={handleShow}
                aria-label={`View payment details for check ${payment.CHECKNUM}`}
            >
                {payment.CHECKNUM}
                <i className="bi bi-eye ms-1 small" aria-hidden="true"></i>
            </button>

            <Modal show={show} onHide={() => setShow(false)} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Payment Details - Check {payment.CHECKNUM}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Table responsive bordered className="mb-0">
                        <tbody>
                            <tr>
                                <th scope="row">Owner #</th>
                                <td>{displayValue(payment.OWNER_NUMBER)}</td>
                            </tr>
                            <tr>
                                <th scope="row">Tax ID</th>
                                <td>{maskTaxId(payment.PAYMENT_TAXID)}</td>
                            </tr>
                            <tr>
                                <th scope="row">Check Date</th>
                                <td>{formatDate(payment.CHECKDATE)}</td>
                            </tr>
                            <tr>
                                <th scope="row">Check Number</th>
                                <td>{displayValue(payment.CHECKNUM)}</td>
                            </tr>
                            <tr>
                                <th scope="row">Amount</th>
                                <td>{formatAmount(payment.AMOUNT)}</td>
                            </tr>
                            <tr>
                                <th scope="row">Address</th>
                                <td>{displayValue(payment.ADDRESS)}</td>
                            </tr>
                            <tr>
                                <th scope="row">City</th>
                                <td>{displayValue(payment.CITY)}</td>
                            </tr>
                            <tr>
                                <th scope="row">Tenant Name</th>
                                <td>{displayValue(payment.TENNAME)}</td>
                            </tr>
                            <tr>
                                <th scope="row">Transaction Type</th>
                                <td>{displayValue(payment.TRANSTYPE)}</td>
                            </tr>
                        </tbody>
                    </Table>
                    <section className="mt-3 p-3 border rounded bg-light" aria-labelledby={`check-total-${payment.CHECKNUM}`}>
                        <h5 id={`check-total-${payment.CHECKNUM}`} className="mb-1">Total Amount Received</h5>
                        {summaryLoading ? (
                            <div className="d-flex align-items-center gap-2" role="status">
                                <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
                                Loading check total...
                            </div>
                        ) : summaryError ? (
                            <p className="text-danger mb-0">The total for this check could not be loaded.</p>
                        ) : checkSummary ? (
                            <>
                                <p className="fs-4 fw-bold mb-1">{formatAmount(checkSummary.totalAmount)}</p>
                                <p className="text-muted mb-0">
                                    {checkSummary.paymentCount} payment {checkSummary.paymentCount === 1 ? 'entry' : 'entries'} included in this check.
                                </p>
                            </>
                        ) : null}
                    </section>
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-secondary" onClick={() => setShow(false)}>
                        Close
                    </button>
                </Modal.Footer>
            </Modal>
        </>
    );
}